/* Pialú · PDP filmstrip (Hay Harper style sticky dock).
   LCP-safe: the dock ships as an empty skeleton; this controller hydrates it AFTER load
   (requestIdleCallback) or when the sticky add-to-cart bar appears, then lazy-loads more
   product thumbnails on horizontal scroll (best-sellers -> rest of catalogue). */
(function () {
  'use strict';

  function idle(cb) {
    if ('requestIdleCallback' in window) requestIdleCallback(cb, { timeout: 2500 });
    else setTimeout(cb, 1200);
  }

  function Filmstrip(dock) {
    if (dock.dataset.pdpInit === '1') return;
    dock.dataset.pdpInit = '1';

    this.dock = dock;
    this.track = dock.querySelector('[data-pdp-filmstrip-track]');
    this.sentinel = dock.querySelector('[data-pdp-filmstrip-sentinel]');
    if (!this.track || !this.sentinel) return;

    this.current = dock.dataset.currentHandle || '';
    this.source = dock.dataset.source || 'avada-best-sellers';
    this.overflow = dock.dataset.overflow || 'all';
    this.max = parseInt(dock.dataset.max, 10) || 120;
    this.visibility = dock.dataset.visibility || 'with_atc';

    this.seen = Object.create(null);
    this.count = 0;
    this.loading = false;
    this.hydrated = false;
    this.phase = 'source'; // 'source' -> 'overflow' -> 'done'
    this.nextUrl = '/collections/' + this.source + '?view=strip-cards';

    this.loadNext = this.loadNext.bind(this);
    this.setupVisibility();
  }

  Filmstrip.prototype.show = function () {
    this.dock.classList.add('is-visible');
    document.body.classList.add(this.visibility === 'always' ? 'pdp-has-dock' : 'pdp-filmstrip-visible');
    this.hydrate();
  };
  Filmstrip.prototype.hide = function () {
    if (this.visibility === 'always') return;
    this.dock.classList.remove('is-visible');
    document.body.classList.remove('pdp-filmstrip-visible');
  };

  Filmstrip.prototype.setupVisibility = function () {
    var self = this;
    if (this.visibility === 'always') {
      // Reserve the dock's space immediately (before hydration) to avoid any layout shift.
      document.body.classList.add('pdp-has-dock');
      idle(function () { self.show(); });
      return;
    }
    // 'with_atc': mirror the sticky add-to-cart bar's visibility (and defer all fetches until then).
    var bar = document.querySelector('.sticky-atc-bar');
    if (!bar) { idle(function () { self.show(); }); return; }
    var sync = function () {
      if (!bar.classList.contains('hidden')) self.show();
      else self.hide();
    };
    this.barObserver = new MutationObserver(sync);
    this.barObserver.observe(bar, { attributes: true, attributeFilter: ['class'] });
    sync();
  };

  Filmstrip.prototype.hydrate = function () {
    if (this.hydrated) return;
    this.hydrated = true;
    var self = this;
    this.loadNext().then(function () {
      self.observe();
      self.centerCurrent();
    });
  };

  Filmstrip.prototype.clearSkeleton = function () {
    var sk = this.track.querySelectorAll('.pdp-filmstrip__skeleton');
    for (var i = 0; i < sk.length; i++) sk[i].parentNode.removeChild(sk[i]);
  };

  Filmstrip.prototype.advancePhase = function () {
    if (this.phase === 'source') {
      this.phase = 'overflow';
      this.nextUrl = '/collections/' + this.overflow + '?view=strip-cards&sort_by=best-selling';
    } else {
      this.phase = 'done';
      this.nextUrl = '';
    }
  };

  Filmstrip.prototype.loadNext = function () {
    var self = this;
    if (this.loading || this.phase === 'done' || !this.nextUrl || this.count >= this.max) {
      return Promise.resolve();
    }
    this.loading = true;
    var url = this.nextUrl;
    return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' }, credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        if (html) self.append(html);
        self.loading = false;
        // If we still have room and just switched phase, keep the chain warm.
      })
      .catch(function () { self.loading = false; });
  };

  Filmstrip.prototype.append = function (html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var items = doc.querySelectorAll('.pdp-filmstrip__item');
    var frag = document.createDocumentFragment();
    var added = 0;
    for (var i = 0; i < items.length; i++) {
      var a = items[i];
      var h = a.getAttribute('data-handle');
      if (!h || this.seen[h]) continue;
      this.seen[h] = 1;
      if (h === this.current) a.classList.add('is-current');
      // imported nodes are inert until adopted; importNode keeps it lightweight
      frag.appendChild(document.importNode(a, true));
      this.count++; added++;
      if (this.count >= this.max) break;
    }
    if (this.firstBatch !== true) { this.firstBatch = true; this.clearSkeleton(); }
    this.track.insertBefore(frag, this.sentinel);

    var more = doc.querySelector('[data-pdp-next]');
    var next = more ? (more.getAttribute('data-pdp-next') || '').replace(/&amp;/g, '&') : '';
    if (next && this.count < this.max) {
      this.nextUrl = next.indexOf('view=') === -1
        ? next + (next.indexOf('?') === -1 ? '?' : '&') + 'view=strip-cards'
        : next;
    } else {
      this.advancePhase();
    }
    // If nothing usable came back (e.g. whole page deduped), keep going to avoid a dead end.
    if (added === 0 && this.phase !== 'done' && this.count < this.max) {
      this.loadNext();
    }
  };

  Filmstrip.prototype.observe = function () {
    var self = this;
    if (!('IntersectionObserver' in window)) {
      // Fallback: load a couple more batches up front.
      this.loadNext().then(function () { self.loadNext(); });
      return;
    }
    this.io = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) self.loadNext();
    }, { root: this.track, rootMargin: '0px 600px 0px 0px', threshold: 0.01 });
    this.io.observe(this.sentinel);
  };

  Filmstrip.prototype.centerCurrent = function () {
    var el = this.track.querySelector('.pdp-filmstrip__item.is-current');
    if (!el) return;
    // Center the current product within the horizontal track.
    var left = el.offsetLeft - (this.track.clientWidth / 2) + (el.clientWidth / 2);
    this.track.scrollTo({ left: left, behavior: 'auto' });
  };

  /* Safari : la barre sticky, profondément imbriquée dans le formulaire produit, est mal compositée et passe
     SOUS les <video> en lecture (Chrome la gère, pas Safari). On la déplace dans la SECTION du dock (même
     contexte de compositing, qui s'affiche correctement sur Safari), en gardant le bouton branché au
     formulaire produit via l'attribut form= et en reflétant l'état dispo/épuisé. */
  function relocateStickyBar() {
    var bar = document.querySelector('.sticky-atc-bar');
    var dock = document.querySelector('.pdp-filmstrip');
    if (!bar || !dock || bar.dataset.pdpRelocated === '1') return;
    var form = bar.closest('form[action*="/cart/add"]') ||
               document.querySelector('form[action*="/cart/add"][data-product-form]') ||
               document.querySelector('form[action*="/cart/add"]');
    if (form) {
      /* NB: form.id est masqué par le <select name="id"> du formulaire (named-property) -> on passe par
         getAttribute/setAttribute pour manipuler le VRAI attribut id. */
      var fid = form.getAttribute('id');
      if (!fid) { fid = 'pdp-form-' + Math.floor(Math.random() * 1e9); form.setAttribute('id', fid); }
      var submit = bar.querySelector('button[name="add"]');
      if (submit) submit.setAttribute('form', fid);
      var inflow = form.querySelector('.pialu-atc-wl-row button[name="add"]') || form.querySelector('.product-form__cart-submit');
      if (inflow && submit && inflow !== submit) {
        var mirror = function () {
          if (inflow.hasAttribute('disabled')) submit.setAttribute('disabled', ''); else submit.removeAttribute('disabled');
          if (inflow.hasAttribute('aria-disabled')) submit.setAttribute('aria-disabled', 'true'); else submit.removeAttribute('aria-disabled');
          var s = submit.querySelector('[data-add-to-cart-text]'), i = inflow.querySelector('[data-add-to-cart-text]');
          if (s && i && s.textContent !== i.textContent) s.textContent = i.textContent;
        };
        try { new MutationObserver(mirror).observe(inflow, { attributes: true, attributeFilter: ['disabled', 'aria-disabled'], childList: true, subtree: true, characterData: true }); } catch (e) {}
        mirror();
      }
    }
    var target = dock.closest('.shopify-section') || dock.parentElement;
    if (target) { target.appendChild(bar); bar.dataset.pdpRelocated = '1'; }
  }

  function init(root) {
    (root || document).querySelectorAll('[data-pdp-filmstrip]').forEach(function (d) {
      new Filmstrip(d);
    });
    relocateStickyBar();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
